import { Box, Container } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CachePolicies } from "use-http";

import { SubNavBar } from "components/common/SubNavBar";
import { DisagreementContent, DisagreementSkeleton, DisagreementWithNotes } from "components/disagreements";
import { SelectDisagreement } from "components/disagreements/SelectDisagreement";
import { JANZU_URL } from "config/config";
import { useFetchCustom } from "hooks/useFetchCustom";
import { useGetDisagreementPermissions } from "hooks/useGetDisagreementPermissions";
import { Typography } from "new-components/typography/Typography";
import { Disagreement } from "types/disagreements";
import { PermissionDisagreements } from "utils/disagreementsUtils";

export function DisagreementsByIdRoute() {
  const [searchParams] = useSearchParams();
  const detectorId = searchParams.get("detector_id");
  const disagreementId = searchParams.get("disagreement_id");

  // fetch Disagreement detail
  const disagreementsUrl = `${JANZU_URL}reef-api/disagreements`;
  const {
    data: disagreementData,
    get: getDisagreement,
    loading: loadingDisagreement,
  } = useFetchCustom<Disagreement>(disagreementsUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  const refetchDisagreement = useCallback(
    (disagreementId: string, detectorId: string) => {
      getDisagreement(`${disagreementId}?detector_id=${detectorId}`);
    },
    [getDisagreement]
  );

  useEffect(() => {
    if (disagreementId && detectorId) {
      refetchDisagreement(disagreementId, detectorId);
    }
  }, [disagreementId, detectorId, refetchDisagreement]);

  const relabelFunc = () => {
    refetchDisagreement(disagreementId as string, detectorId as string);
  };

  return (
    <Container>
      {loadingDisagreement ? (
        <DisagreementSkeleton />
      ) : (
        <Box>
          {disagreementData && typeof disagreementData === "object" && disagreementData?.id ? (
            <DisagreementContent isLargeMode disagreementData={disagreementData} onFinishRelabel={relabelFunc} />
          ) : (
            <Box className="h-[70vh] flex justify-center items-center">
              <Typography fontWeight="bold">This disagreement does not exist</Typography>
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
}

export function DisagreementsView() {
  const permission = useGetDisagreementPermissions();
  const [error, setError] = useState<string | null>(null);
  const [currentDisagreement, setCurrentDisagreement] = useState<Disagreement | null>(null);
  const [shouldRefetch, setShouldRefetch] = useState<boolean>(false);

  const relabelFunc = () => {
    setShouldRefetch(!shouldRefetch);
    setCurrentDisagreement(null);
  };

  return (
    <Box>
      <SubNavBar>
        <div className="flex flex-row w-full justify-between">
          <div className="flex flex-row">
            <SelectDisagreement
              currentDisagreement={currentDisagreement}
              setCurrentDisagreement={setCurrentDisagreement}
              shouldRefetch={shouldRefetch}
              setShouldRefetch={setShouldRefetch}
              showCloseDisagreements={permission === PermissionDisagreements.Manager}
              setError={setError}
            />
          </div>
        </div>
      </SubNavBar>
      <Container>
        {/* eslint-disable-next-line */}
        {currentDisagreement ? (
          permission === PermissionDisagreements.Manager ? (
            <DisagreementContent isLargeMode disagreementData={currentDisagreement} onFinishRelabel={relabelFunc} />
          ) : (
            <DisagreementWithNotes
              currentDisagreement={currentDisagreement}
              isLargeMode={false}
              onFinishRelabel={relabelFunc}
            />
          )
        ) : (
          <Box className="h-[70vh] flex justify-center items-center">
            <Typography fontWeight="bold">{error || "Select a disagreement"}</Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}
